/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Card, Col, Image, Radio, Row, Switch } from "antd";
import {
  InputField,
  InputTextArea,
  SelectField,
} from "../../../../../../atoms/FormFields";
import { useState } from "react";
import UploadFull from "../../../../../../atoms/UploadPhoto/UploadFull";
import Meta from "antd/es/card/Meta";
import { RefreshIcon } from "../../../../../../atoms/CustomIcons";
import { FieldErrors } from "react-hook-form";
import { useAppSelector } from "../../../../../../config/hooks";

// Define or import the FormValues type
interface FormValues {
  name: string;
  description?: string;
  frequency?: string;
  quantity_unit?: string;
  take_off?: string;
  production_rate?: number;
  subcontractors?: string[];
}

interface PropsType {
  control: any;
  setLoading: (loading: boolean) => void;
  errors: FieldErrors<FormValues>;
  serviceType: string;
  setValue: any;
  frequencyType: string;
  takeOff: boolean;
  checklistFile: File | null;
  type: string | null;
  isTakeOff: React.Dispatch<React.SetStateAction<boolean>>;
  setFrequencyType: React.Dispatch<React.SetStateAction<string>>;
  setServiceType: React.Dispatch<React.SetStateAction<string>>;
  setChecklistFile: React.Dispatch<React.SetStateAction<File | null>>;
  setTakeOffType: React.Dispatch<React.SetStateAction<string>>;
  frequencyTypeData?:
    | {
        frequency: string;
      }
    | undefined;
}

const frequencyList: { value: string; label: string }[] = [
  { value: "weekly", label: "Weekly" },
  { value: "bi_weekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];

export default function GeneralTab(props: PropsType) {
  const {
    control,
    setLoading,
    errors,
    setServiceType,
    serviceType,
    takeOff,
    type,
    isTakeOff,
    frequencyType,
    checklistFile,
    setChecklistFile,
    setFrequencyType,
    setValue,
    setTakeOffType,
    frequencyTypeData,
  } = props;
  const serviceFormData = useAppSelector(
    (state) => state?.services?.serviceFormData
  );

  const [previewImage, setPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });
  const [modalPreviewImage, setModalPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });

  const saveUpload = () => {
    setLoading(true);
    setPreviewImage({ ...modalPreviewImage });
    setLoading(false);
  };

  const onChange = (checked: boolean) => {
    isTakeOff(checked);
  };

  const onSelectChange = (value: string | number | readonly string[]) => {
    const selectedTakeOff: any =
      serviceFormData?.data?.takeoff &&
      serviceFormData?.data?.takeoff?.find(
        (takeOff: { value: string }) => takeOff?.value === value
      );
    setTakeOffType(selectedTakeOff?.unit);
    if (selectedTakeOff && selectedTakeOff?.unit) {
      setValue(
        "take_off_unit",
        `${selectedTakeOff?.unit} (${selectedTakeOff?.unit_name})`
      );
    } else {
      setValue("take_off_unit", "");
    }
  };

  const onSelectChangeUnit = (value: string | number | readonly string[]) => {
    const selectedTakeOff: any =
      serviceFormData?.data?.units &&
      serviceFormData?.data?.units?.find(
        (unit: { value: string }) => unit?.value === value
      );
    setTakeOffType(selectedTakeOff?.unit);
  };

  return (
    <div className="tabs_content">
      <Card className="service_tabs_card">
        <h4 style={{ marginBottom: "16px" }}>General details</h4>
        <Row>
          <Col span={24} style={{ marginBottom: "24px" }}>
            <UploadFull
              saveUpload={saveUpload}
              loading={false}
              onImageDelete={() => null}
              previewImage={previewImage}
              checklistFile={checklistFile}
              modalPreviewImage={modalPreviewImage}
              setChecklistFile={setChecklistFile}
              setPreviewImage={setPreviewImage}
              setModalPreviewImage={setModalPreviewImage}
              infoTextUploader="Use the picture in proposals and help others identify this service. It’s optional."
            />
          </Col>
          <Col span={24}>
            <InputField
              fieldName="name"
              label="Service name"
              control={control}
              defaultValue=""
              iProps={{
                placeholder: "Enter Service name",
              }}
              isRequired
              rules={{
                required: "Service name is required.",
              }}
              validate={errors.name && "error"}
              validMessage={errors.name && errors.name.message}
            />
          </Col>
          <Col span={24}>
            <InputTextArea
              fieldName="description"
              label="Description"
              control={control}
              defaultValue=""
              iProps={{
                placeholder: "A brief description for internal purposes ...",
              }}
              tooltipTitle="This will be visible to your customers."
            />
          </Col>

          {type === "simple" && (
            <Col span={24}>
              <div className="label_text" style={{ paddingBottom: 6 }}>
                How the service is provided:
              </div>
              <div
                className="service_type_card service_line_card"
                style={{
                  paddingBottom: frequencyTypeData?.frequency ? 0 : "20px",
                }}
              >
                <Radio.Group
                  onChange={(e) => setServiceType(e.target.value)}
                  value={serviceType}
                >
                  <Radio.Button value="in_house">
                    <Card>
                      <Meta
                        avatar={
                          <Image
                            src="/images/services/in-house-icon.svg"
                            alt="filled"
                            preview={false}
                            width={56}
                            height={56}
                          />
                        }
                        title="In-house"
                        description="Performed by the company using internal labor."
                      />

                      <div className="checkbox_image">
                        <Image
                          src="/images/services/radio-filled.svg"
                          alt="filled"
                          preview={false}
                          className="checkbox-filled"
                        />
                        <Image
                          src="/images/services/radio-unfilled.svg"
                          alt="un-filled"
                          preview={false}
                          className="checkbox-unfilled"
                        />
                      </div>
                    </Card>
                  </Radio.Button>
                  <Radio.Button value="subcontracted">
                    <Card>
                      <Meta
                        avatar={
                          <Image
                            src="/images/services/subcontracted-icon.svg"
                            alt="filled"
                            preview={false}
                            width={56}
                            height={56}
                          />
                        }
                        title="Subcontracted"
                        description="Outsourced to an external vendor or contractor."
                      />

                      <div className="checkbox_image">
                        <Image
                          src="/images/services/radio-filled.svg"
                          alt="filled"
                          preview={false}
                          className="checkbox-filled"
                        />
                        <Image
                          src="/images/services/radio-unfilled.svg"
                          alt="un-filled"
                          preview={false}
                          className="checkbox-unfilled"
                        />
                      </div>
                    </Card>
                  </Radio.Button>
                </Radio.Group>
              </div>
            </Col>
          )}
          {!frequencyTypeData && (
            <Col span={24}>
              <div className="label_text" style={{ paddingBottom: 6 }}>
                Service frequency:
              </div>
              <div
                className="service_type_card service_line_card"
                style={{ paddingBottom: 0 }}
              >
                <Radio.Group
                  onChange={(e) => setFrequencyType(e.target.value)}
                  value={frequencyType}
                >
                  <Radio.Button value="one_time">
                    <Card>
                      <Meta
                        avatar={
                          <Image
                            src="/images/services/time-icon.svg"
                            alt="filled"
                            preview={false}
                            width={56}
                            height={56}
                          />
                        }
                        title="One-time"
                        description="Performed once per contract, such as a spring clean-up or an installation job."
                      />

                      <div className="checkbox_image">
                        <Image
                          src="/images/services/radio-filled.svg"
                          alt="filled"
                          preview={false}
                          className="checkbox-filled"
                        />
                        <Image
                          src="/images/services/radio-unfilled.svg"
                          alt="un-filled"
                          preview={false}
                          className="checkbox-unfilled"
                        />
                      </div>
                    </Card>
                  </Radio.Button>
                  <Radio.Button value="recurring">
                    <Card>
                      <Meta
                        avatar={
                          <Image
                            src="/images/services/time-icon.svg"
                            alt="filled"
                            preview={false}
                            width={56}
                            height={56}
                          />
                        }
                        title="Recurring"
                        description="Performed regularly during visits, like a weekly service or irrigation wet check."
                      />

                      <div className="checkbox_image">
                        <Image
                          src="/images/services/radio-filled.svg"
                          alt="filled"
                          preview={false}
                          className="checkbox-filled"
                        />
                        <Image
                          src="/images/services/radio-unfilled.svg"
                          alt="un-filled"
                          preview={false}
                          className="checkbox-unfilled"
                        />
                      </div>
                    </Card>
                  </Radio.Button>
                </Radio.Group>
              </div>
            </Col>
          )}
        </Row>
      </Card>
      {(frequencyType === "recurring" ||
        frequencyTypeData?.frequency === "recurring") &&
        type === "simple" && (
          <Card>
            <div className="card_top">
              <div className="card_left">
                <h4
                  style={{
                    marginBottom: "4px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  Recurrence settings
                  <Button className="refreshIcon">
                    <RefreshIcon />
                  </Button>
                </h4>
                <p style={{ marginBottom: "16px" }}>
                  Set how frequently the service is provided. Leave empty if it
                  varies by property or none of the suggested options work.
                  Managers will specify the number of visits directly in the
                  estimates.
                </p>
              </div>
            </div>

            <SelectField
              fieldName="frequency"
              label="Frequency"
              control={control}
              selectOption={frequencyList}
              iProps={{
                placeholder: "Choose frequency",
              }}
              className="bottom_0"
              isRequired
              rules={{
                required: "Frequency is required.",
              }}
              validate={errors.frequency && "error"}
              validMessage={errors.frequency && errors.frequency.message}
            />
          </Card>
        )}

      {type === "simple" && (
        <Card>
          <h4 style={{ marginBottom: "4px" }}>Service quantity measurement</h4>
          <p style={{ marginBottom: "16px" }}>
            Define service quantity units (e.g. square foot, cubic yard) to
            calculate its costs in estimates.
          </p>

          <div
            className="switchBox"
            style={{
              marginBottom: "16px",
              padding: "10px 0",
              fontSize: "14px",
            }}
          >
            <Switch onChange={onChange} checked={takeOff} />
            Use property takeoff measurement
          </div>

          {takeOff ? (
            <Row gutter={8}>
              <Col span={14}>
                <SelectField
                  fieldName="take_off"
                  label="Takeoff measurement"
                  control={control}
                  selectOption={serviceFormData?.data?.takeoff ?? []}
                  iProps={{
                    placeholder: "Select unit",
                  }}
                  onSelectChange={onSelectChange}
                  className="bottom_0"
                  isRequired
                  rules={{
                    required: "Takeoff measurement is required.",
                  }}
                  validate={errors.take_off && "error"}
                  validMessage={errors.take_off && errors.take_off.message}
                />
              </Col>
              <Col span={10}>
                <InputField
                  fieldName="take_off_unit"
                  label="Service quantity unit"
                  control={control}
                  iProps={{
                    placeholder: "Quantity unit",
                    disabled: true,
                  }}
                  className="bottom_0"
                />
              </Col>
            </Row>
          ) : (
            <SelectField
              fieldName="quantity_unit"
              label="Service quantity unit"
              control={control}
              selectOption={
                serviceFormData?.data?.units &&
                serviceFormData?.data?.units?.length > 0
                  ? serviceFormData?.data?.units?.map(
                      (unit: {
                        unit: string;
                        label: string;
                        value: string;
                      }) => ({
                        label: `${unit?.unit} (${unit?.label})`,
                        value: unit?.value ?? unit?.unit,
                      })
                    )
                  : []
              }
              iProps={{
                placeholder: "Select unit",
              }}
              onSelectChange={onSelectChangeUnit}
              className="bottom_0"
              isRequired
              rules={{
                required: "Service quantity unit is required.",
              }}
              validate={errors.quantity_unit && "error"}
              validMessage={
                errors.quantity_unit && errors.quantity_unit.message
              }
            />
          )}
        </Card>
      )}
    </div>
  );
}
